import { useContent } from "../hooks/useContent";
import { useInView } from "../hooks/useInView";
import { MapPin, Phone, Navigation, MessageCircle } from "lucide-react";
import { Badge } from "./ui/badge";

export const StoresSection = () => {
  const [ref, isInView] = useInView();
  const { getSection } = useContent();
  const stores = getSection("stores") || [];

  return (
    <section id="stores" className="py-16 lg:py-20 bg-white relative overflow-hidden">
      {/* Subtle laundry-themed accents */}
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-lime-50/30 rounded-full blur-3xl -translate-x-1/3 -translate-y-1/3 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[420px] h-[420px] bg-sky-50/40 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12" ref={ref}>
          <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-4">
            Our Stores
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-5">
            Now in Jaipur,
            <br />
            <span className="text-sky-600">Expanding Across India</span>
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed">
            Currently serving Jaipur with 2 stores and 3 more on the way. Each store has its own dedicated phone & WhatsApp.
          </p>
        </div>

        <div
          className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto transition-all duration-700 ${
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {stores.map((store, i) => (
            <div
              key={i}
              className={`relative rounded-2xl border p-6 transition-all duration-300 ${
                store.status === "open"
                  ? "bg-white border-sky-200 shadow-md shadow-sky-900/5 hover:shadow-lg"
                  : "bg-slate-50/50 border-slate-200 border-dashed"
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
              data-testid={`store-card-${i}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    store.status === "open" ? "bg-sky-50" : "bg-slate-100"
                  }`}
                >
                  <MapPin
                    className={`w-5 h-5 ${
                      store.status === "open" ? "text-sky-600" : "text-slate-400"
                    }`}
                  />
                </div>
                <Badge
                  className={`text-xs font-semibold ${
                    store.status === "open"
                      ? "bg-lime-50 text-lime-600 border-lime-200 hover:bg-lime-50"
                      : "bg-orange-50 text-orange-500 border-orange-200 hover:bg-orange-50"
                  }`}
                >
                  {store.status === "open" ? "Open Now" : "Coming Soon"}
                </Badge>
              </div>

              <h3
                className={`text-lg font-bold mb-1 ${
                  store.status === "open" ? "text-slate-900" : "text-slate-500"
                }`}
              >
                {store.name}
              </h3>
              <p
                className={`text-sm mb-4 ${
                  store.status === "open" ? "text-slate-500" : "text-slate-400"
                }`}
              >
                {store.area}
              </p>

              {store.status === "open" && (
                <div className="space-y-2.5">
                  {store.phone && (
                    <a
                      href={`tel:${store.phone.replace(/\s/g, "")}`}
                      className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold transition-colors shadow-sm shadow-sky-600/20"
                      data-testid={`store-call-${i}`}
                    >
                      <Phone className="w-4 h-4" />
                      Call {store.name}
                    </a>
                  )}
                  {store.whatsapp && (
                    <a
                      href={store.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition-colors shadow-sm shadow-green-500/20"
                      data-testid={`store-whatsapp-${i}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp {store.name}
                    </a>
                  )}
                  {store.mapLink && (
                    <a
                      href={store.mapLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl bg-white border border-lime-300 text-lime-700 hover:bg-lime-50 text-sm font-semibold transition-colors"
                      data-testid={`store-map-${i}`}
                    >
                      <Navigation className="w-4 h-4" />
                      View on Map
                    </a>
                  )}
                </div>
              )}

              {store.status !== "open" && (
                <p className="text-xs text-slate-400 mt-2">Opening soon — stay tuned.</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
